import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  CATEGORY_BACKGROUND_COLOR_MAP,
  CATEGORY_COLOR_MAP,
  VersionAttributeConflictBreakdownRow,
  VersionAttributeConflictDetail,
  VersionAttributeConflictPercentBar,
  VersionAttributeConflictPercentBarSegment,
  VersionAttributeConflictValueCoverage,
  VersionAttributeFilterPair,
  VersionAttributeFilterSelection,
} from '../../models/store-attribute.model';
import { VersionConflictDetailService } from '../../services/version-conflict-detail.service';

const SEGMENT_PALETTE = ['#1976d2', '#7b1fa2', '#00897b', '#f57c00', '#6d4c41', '#c2185b'];
const SEGMENT_NA_COLOR = '#9e9e9e';
const SEGMENT_MATCH_COLOR = '#2e7d32';
const SEGMENT_REST_COLOR = '#e9ecef';
const NA_SEGMENT_VALUE = '[N/A]';

@Component({
  selector: 'app-version-conflict-detail-panel',
  templateUrl: './version-conflict-detail-panel.component.html',
  styleUrls: ['./version-conflict-detail-panel.component.scss'],
})
export class VersionConflictDetailPanelComponent implements OnInit, OnDestroy {
  @Input() eventId = '';
  @Input() versionId = '';
  @Input() versionName = '';
  @Input() appliedFilters: VersionAttributeFilterSelection[] = [];
  @Input() filterPairs: VersionAttributeFilterPair[] = [];
  @Input() showActions = false;

  @Output() excludeVersion = new EventEmitter<void>();
  @Output() includeAnyway = new EventEmitter<void>();

  detail: VersionAttributeConflictDetail | null = null;
  loading = true;
  loadError = false;

  private subscription?: Subscription;

  constructor(private conflictDetailService: VersionConflictDetailService) {}

  ngOnInit(): void {
    if (!this.eventId || !this.versionId || !this.filterPairs.length) {
      this.loading = false;
      this.loadError = true;
      return;
    }

    this.subscription = this.conflictDetailService
      .getConflictDetail(this.eventId, this.versionId, this.filterPairs)
      .subscribe((detail) => {
        this.detail = detail;
        this.loading = false;
        this.loadError = detail == null;
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  get totalStoresCount(): number {
    return this.detail?.totalStoresCount ?? 0;
  }

  getRowTone(row: VersionAttributeConflictBreakdownRow): 'success' | 'warning' | 'danger' {
    if (!row.cells.length) {
      return 'success';
    }

    if (row.cells.every((cell) => cell.matchedCount >= cell.totalCount)) {
      return 'success';
    }

    if (row.cells.every((cell) => cell.matchedCount === 0)) {
      return 'danger';
    }

    return 'warning';
  }

  getFilterChipStyle(filter: VersionAttributeFilterSelection): Record<string, string> {
    return {
      background: CATEGORY_BACKGROUND_COLOR_MAP[filter.categoryColor],
      color: CATEGORY_COLOR_MAP[filter.categoryColor],
    };
  }

  getCoverageChipStyle(attr: VersionAttributeConflictValueCoverage): Record<string, string> {
    return {
      background: attr.backgroundColorHex,
      color: attr.colorHex,
    };
  }

  getCoverageLabel(attr: VersionAttributeConflictValueCoverage): string {
    const value = attr.value.toLowerCase();
    const isBooleanYes = value === 'true' || value === 'yes';
    const name = isBooleanYes ? attr.name : `${attr.name}: ${attr.value}`;
    return `${name} (${attr.percent}%)`;
  }

  //boolean and multi-select per-value bars are a matched / not-matched split
  isBinaryBar(bar: VersionAttributeConflictPercentBar): boolean {
    return bar.displayMode === 'Boolean' || bar.displayMode === 'MultiSelect';
  }

  getSegmentColor(
    bar: VersionAttributeConflictPercentBar,
    segment: VersionAttributeConflictPercentBarSegment,
    index: number,
  ): string {
    if (this.isBinaryBar(bar)) {
      return index === 0 ? SEGMENT_MATCH_COLOR : SEGMENT_REST_COLOR;
    }

    if (segment.value === NA_SEGMENT_VALUE) {
      return SEGMENT_NA_COLOR;
    }

    return SEGMENT_PALETTE[index % SEGMENT_PALETTE.length];
  }

  getSegmentTextColor(bar: VersionAttributeConflictPercentBar, index: number): string {
    return this.isBinaryBar(bar) && index > 0 ? 'rgba(0, 0, 0, 0.6)' : '#fff';
  }

  getSegmentLabel(
    bar: VersionAttributeConflictPercentBar,
    segment: VersionAttributeConflictPercentBarSegment,
    index: number,
  ): string {
    if (this.isBinaryBar(bar)) {
      const percentText = `${segment.percent}%`;
      return segment.count != null ? `${segment.count} (${percentText})` : percentText;
    }

    return segment.count != null
      ? `${segment.value}: ${segment.count} (${segment.percent}%)`
      : `${segment.value} (${segment.percent}%)`;
  }

  getSegmentTooltip(
    bar: VersionAttributeConflictPercentBar,
    segment: VersionAttributeConflictPercentBarSegment,
    index: number,
  ): string {
    const name = this.isBinaryBar(bar)
      ? index === 0 ? 'Matched' : 'Not matched'
      : segment.value;

    return segment.count != null
      ? `${name}: ${segment.count} stores (${segment.percent}%)`
      : `${name}: ${segment.percent}%`;
  }

  onExclude(): void {
    this.excludeVersion.emit();
  }

  onIncludeAnyway(): void {
    this.includeAnyway.emit();
  }
}
